import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { projetosApi, type AvaliacaoCriterio } from '../../api/projetos.js';

interface ModalGateReviewProps {
  faseId: string;
  onFechar: () => void;
}

// SPEC-UX §4.5 — GateReview é uma tela de decisão: critérios um a um,
// atendido/não + evidência, resultado com justificativa em reprovação.
// Reprovação devolve a fase com o motivo visível (mostrado no card da fase
// via estado — a UI não duplica esse texto aqui).
export function ModalGateReview({ faseId, onFechar }: ModalGateReviewProps) {
  const queryClient = useQueryClient();
  const { data: criterios } = useQuery({ queryKey: ['fase', faseId, 'criterios'], queryFn: () => projetosApi.criteriosGate(faseId) });
  const [avaliacoes, setAvaliacoes] = useState<Record<string, AvaliacaoCriterio>>({});
  const [justificativa, setJustificativa] = useState('');

  const avaliar = useMutation({
    mutationFn: (resultado: 'Aprovado' | 'Reprovado') =>
      projetosApi.avaliarGate(faseId, Object.values(avaliacoes), resultado, resultado === 'Reprovado' ? justificativa : undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projeto'] });
      onFechar();
    },
  });

  function marcar(criterioId: string, atendido: boolean) {
    setAvaliacoes((prev) => ({ ...prev, [criterioId]: { criterioId, atendido, evidencia: prev[criterioId]?.evidencia } }));
  }

  const todosAvaliados = criterios ? criterios.every((c) => avaliacoes[c.id] !== undefined) : false;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-lg rounded-lg border border-borda bg-superficie p-5 shadow-xl">
        <h2 className="text-base font-semibold text-ink">Avaliar gate</h2>

        <ul className="mt-4 flex flex-col gap-3">
          {criterios?.map((criterio) => (
            <li key={criterio.id} className="rounded border border-borda p-3">
              <p className="text-sm text-ink">
                {criterio.descricao} {criterio.obrigatorio && <span className="text-status-vermelho">*</span>}
              </p>
              <div className="mt-2 flex gap-2">
                <button
                  type="button"
                  onClick={() => marcar(criterio.id, true)}
                  className={`rounded px-2 py-1 text-xs ${avaliacoes[criterio.id]?.atendido === true ? 'bg-status-verde/20 text-status-verde' : 'border border-borda text-grafite'}`}
                >
                  Atendido
                </button>
                <button
                  type="button"
                  onClick={() => marcar(criterio.id, false)}
                  className={`rounded px-2 py-1 text-xs ${avaliacoes[criterio.id]?.atendido === false ? 'bg-status-vermelho/20 text-status-vermelho' : 'border border-borda text-grafite'}`}
                >
                  Não atendido
                </button>
              </div>
              <input
                placeholder="Evidência"
                value={avaliacoes[criterio.id]?.evidencia ?? ''}
                onChange={(e) =>
                  setAvaliacoes((prev) => ({
                    ...prev,
                    [criterio.id]: { criterioId: criterio.id, atendido: prev[criterio.id]?.atendido ?? false, evidencia: e.target.value },
                  }))
                }
                className="mt-2 w-full rounded border border-borda bg-base-100 px-2 py-1 text-xs text-ink"
              />
            </li>
          ))}
        </ul>

        <label className="mt-4 block text-sm">
          <span className="text-ink">Justificativa (obrigatória em reprovação)</span>
          <textarea
            value={justificativa}
            onChange={(e) => setJustificativa(e.target.value)}
            rows={2}
            className="mt-1 w-full rounded border border-borda bg-base-100 px-3 py-2 text-sm text-ink"
          />
        </label>

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onFechar} className="rounded px-3 py-2 text-sm text-grafite hover:bg-borda/40">
            Cancelar
          </button>
          <button
            type="button"
            disabled={!todosAvaliados || avaliar.isPending}
            onClick={() => avaliar.mutate('Reprovado')}
            className="rounded border border-status-vermelho px-4 py-2 text-sm font-medium text-status-vermelho hover:bg-status-vermelho/5 disabled:opacity-50"
          >
            Reprovar
          </button>
          <button
            type="button"
            disabled={!todosAvaliados || avaliar.isPending}
            onClick={() => avaliar.mutate('Aprovado')}
            className="rounded bg-aco px-4 py-2 text-sm font-medium text-white hover:bg-aco-700 disabled:opacity-50"
          >
            Aprovar
          </button>
        </div>
      </div>
    </div>
  );
}
